import type { ActivatedResource } from '@/features/activation/types/activatedResource'
import type { Activation } from '@/features/activation/types/activation'
import { logMessageInCoralogix } from '@/features/coralogix/utils/sendExceptions'

/**
 * Derives the site-specific activation time range from individual resource timestamps.
 *
 * The backend returns portfolio-level startedAt/endedAt on the activation, which spans
 * all sites and resources. Individual site resources may have been activated later and deactivated earlier.
 * This function computes the actual time range for a specific site by finding the earliest
 * activatedAt and latest deactivatedAt across its resources.
 *
 * Falls back to the activation-level startedAt/endedAt when resource timestamps are unavailable.
 */
export function getSiteActivationTimeRange(
  activatedResources: ActivatedResource[] | undefined,
  activation: Activation | undefined,
) {
  if (!activatedResources || activatedResources.length === 0) {
    const noResourcesMessage = `Activation ${activation?.id} has no activated resources. Falling back to activation time range`
    logMessageInCoralogix(noResourcesMessage)
    return {
      startedAt: activation?.startedAt,
      endedAt: activation?.endedAt,
    }
  }

  const activatedTimes = activatedResources.map((r) => r.activatedAt).filter((t): t is string => !!t)
  const deactivatedTimes = activatedResources.map((r) => r.deactivatedAt).filter((t): t is string => !!t)

  if (activatedTimes.length === 0 && deactivatedTimes.length === 0) {
    const missingTimestampsMessage = `Activation ${activation?.id} has resources but all are missing timestamps. Falling back to activation time range`
    logMessageInCoralogix(missingTimestampsMessage)
  }

  return {
    startedAt: [...activatedTimes].sort((a, b) => a.localeCompare(b)).at(0) ?? activation?.startedAt,
    endedAt: [...deactivatedTimes].sort((a, b) => a.localeCompare(b)).at(-1) ?? activation?.endedAt,
  }
}
